import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { provisioningService } from "../../services/provisioningService";
import { required_error } from "../../services/shared";

// Step 1 schema
const step1Schema = z.object({
  name: z.string({ required_error }).min(2, "Minimo 2 caratteri"),
  wa_phone_number_id: z.string({ required_error }).min(1, "Campo obbligatorio"),
  wa_verify_token: z.string({ required_error }).min(1, "Campo obbligatorio"),
});

// Step 2 schema
const step2Schema = z.object({
  wa_access_token_value: z.string({ required_error }).min(1, "Campo obbligatorio"),
  openai_api_key_value: z
    .string({ required_error })
    .min(1, "Campo obbligatorio")
    .refine((v) => v.startsWith("sk-"), { message: 'La chiave OpenAI deve iniziare con "sk-"' }),
});

type Step1Form = z.infer<typeof step1Schema>;
type Step2Form = z.infer<typeof step2Schema>;

const STEPS = ["Informazioni base", "Credenziali"];

export default function TenantCreate() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [step1Data, setStep1Data] = useState<Step1Form | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form1 = useForm<Step1Form>({
    resolver: zodResolver(step1Schema),
    defaultValues: { name: "", wa_phone_number_id: "", wa_verify_token: "" },
  });

  const form2 = useForm<Step2Form>({
    resolver: zodResolver(step2Schema),
    defaultValues: { wa_access_token_value: "", openai_api_key_value: "" },
  });

  const handleStep1 = form1.handleSubmit((data) => {
    setStep1Data(data);
    setActiveStep(1);
  });

  const handleStep2 = form2.handleSubmit(async (data) => {
    if (!step1Data) return;
    setSubmitting(true);
    setError(null);
    try {
      await provisioningService.createTenant({ ...step1Data, ...data });
      navigate("/chatbot/tenants");
    } catch (e) {
      console.error(e);
      setError("Errore durante la creazione del tenant");
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="container">
      <FormLabel label="Nuovo Tenant" />

      <Box
        sx={{
          background: "white",
          border: "1px solid #d1d1d1",
          borderRadius: ".5em",
          p: 3,
          mb: 2,
        }}
        className="flex-col gap-2"
      >
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <form onSubmit={handleStep1} className="flex-col gap-2">
            <Controller
              name="name"
              control={form1.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Nome tenant"
                  placeholder="Es. Affida SRL"
                  error={!!form1.formState.errors.name}
                  helperText={form1.formState.errors.name?.message}
                />
              )}
            />
            <Controller
              name="wa_phone_number_id"
              control={form1.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="WhatsApp Phone Number ID"
                  placeholder="Es. 123456789012345"
                  error={!!form1.formState.errors.wa_phone_number_id}
                  helperText={form1.formState.errors.wa_phone_number_id?.message}
                />
              )}
            />
            <Controller
              name="wa_verify_token"
              control={form1.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="WhatsApp Verify Token"
                  placeholder="Token di verifica webhook"
                  error={!!form1.formState.errors.wa_verify_token}
                  helperText={form1.formState.errors.wa_verify_token?.message}
                />
              )}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={() => navigate("/chatbot/tenants")}>
                Annulla
              </Button>
              <Button variant="contained" type="submit">
                Avanti
              </Button>
            </Box>
          </form>
        )}

        {activeStep === 1 && (
          <form onSubmit={handleStep2} className="flex-col gap-2">
            <Controller
              name="wa_access_token_value"
              control={form2.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  type="password"
                  label="WhatsApp Access Token"
                  placeholder="Token di accesso WhatsApp Business"
                  error={!!form2.formState.errors.wa_access_token_value}
                  helperText={form2.formState.errors.wa_access_token_value?.message}
                />
              )}
            />
            <Controller
              name="openai_api_key_value"
              control={form2.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  type="password"
                  label="OpenAI API Key"
                  placeholder="sk-..."
                  error={!!form2.formState.errors.openai_api_key_value}
                  helperText={form2.formState.errors.openai_api_key_value?.message}
                />
              )}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={() => setActiveStep(0)} disabled={submitting}>
                Indietro
              </Button>
              <Button variant="contained" type="submit" disabled={submitting}>
                {submitting ? <CircularProgress size={20} /> : "Crea tenant"}
              </Button>
            </Box>
          </form>
        )}
      </Box>
    </div>
  );
}
